// components/TinyMCEEditor.jsx - Enhanced with table preview fix and backend auto-save
import React, { useEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import {
  ArrowUndo24Regular,
  ArrowRedo24Regular,
  TextFont24Filled,
  PaintBrush24Regular,
  BorderAll24Filled,
  Table24Filled,
  Emoji24Filled,
  Symbols24Filled,
  TableInsertRow24Filled,
  TableInsertColumn24Filled,
  TableDeleteRow24Filled,
  TableDeleteColumn24Filled,
  TableCellsMerge24Filled,
  TableCellsSplit24Filled,
  TextBold24Filled,
  TextItalic24Filled,
  TextUnderline24Filled,
  TextStrikethrough24Filled,
  ColorFill24Filled,
  Highlight24Filled
} from '@fluentui/react-icons';
import { renderToStaticMarkup } from 'react-dom/server';

// Helper function to convert React icon to SVG string for TinyMCE
const getIconSvg = (IconComponent) => {
  const markup = renderToStaticMarkup(<IconComponent />);
  // Extract just the SVG content without the wrapper
  return markup;
};

const TinyMCEEditor = ({
  initialValue,
  onEditorChange,
  height = 500,
  disabled = false,
  editorRef = null,
  onSelectionChange = null,
  value = null, // Controlled component support
  customStyles = '', // Original styles from HTML
  onAutoSave = null, // Auto-save callback (debounced backend save)
  autoSaveDelay = 5000 // Auto-save delay in milliseconds
}) => {
  const autoSaveTimerRef = useRef(null);

  // Function to extract styles from HTML content
  const extractStylesFromHTML = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Find all style tags
    const styleTags = tempDiv.querySelectorAll('style');
    let extractedStyles = '';
    
    styleTags.forEach(styleTag => {
      extractedStyles += styleTag.textContent + '\n';
    });
    
    return extractedStyles;
  };

  // Use provided content and styles
  const contentToUse = value || initialValue;
  // Use passed customStyles or extract from content as fallback
  const stylesToApply = customStyles || extractStylesFromHTML(contentToUse);

  // Fix TinyMCE UI elements in fullscreen mode and compact toolbar styling
  useEffect(() => {
    // Add global styles to ensure TinyMCE UI appears on top in fullscreen
    const styleId = 'tinymce-fullscreen-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Ensure TinyMCE UI elements appear above fullscreen container */
        .tox.tox-silver-sink.tox-tinymce-aux {
          z-index: 2147483647 !important;
        }
        .tox-dialog-wrap {
          z-index: 2147483647 !important;
        }
        .tox-dialog {
          z-index: 2147483647 !important;
        }
        .tox-menu {
          z-index: 2147483647 !important;
        }
        .tox-collection {
          z-index: 2147483647 !important;
        }
        .tox-toolbar-overlord {
          z-index: 2147483646 !important;
        }

        /* Compact toolbar styling */
        .tox .tox-toolbar,
        .tox .tox-toolbar__overflow,
        .tox .tox-toolbar__primary {
          padding: 2px 4px !important;
          min-height: 32px !important;
          background: #f9fafb !important;
        }

        .tox .tox-tbtn {
          height: 26px !important;
          width: 26px !important;
          margin: 1px !important;
        }

        .tox .tox-tbtn svg {
          height: 14px !important;
          width: 14px !important;
        }

        .tox .tox-toolbar__group {
          padding: 0 2px !important;
        }

        .tox .tox-tbtn--select {
          width: auto !important;
          min-width: 60px !important;
          padding: 0 4px !important;
        }

        .tox .tox-tbtn__select-label {
          font-size: 11px !important;
          padding: 0 2px !important;
        }

        /* Compact menubar */
        .tox .tox-menubar {
          padding: 2px 4px !important;
          background: #f3f4f6 !important;
        }

        .tox .tox-mbtn {
          height: 24px !important;
          padding: 0 6px !important;
          margin: 1px !important;
          font-size: 11px !important;
        }

        .tox .tox-mbtn__select-label {
          font-size: 11px !important;
        }

        /* Compact statusbar */
        .tox .tox-statusbar {
          padding: 2px 8px !important;
          font-size: 10px !important;
          min-height: 20px !important;
        }

        .tox .tox-statusbar__text-container {
          font-size: 10px !important;
        }

        /* Compact editor container borders */
        .tox .tox-editor-header {
          border-bottom: 1px solid #e5e7eb !important;
        }

        .tox:not(.tox-tinymce-inline) .tox-editor-header {
          box-shadow: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      // Cleanup on unmount
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  return (
    <Editor
      apiKey='glp'
      tinymceScriptSrc="https://cdn.tiny.cloud/1/wzmtuezo8dx7njg6xmfl8qmi047go4w33rop5s645mn1lqj7/tinymce/6/tinymce.min.js"
      onInit={(evt, editor) => {
        if (editorRef) {
          editorRef.current = editor;
        }
        console.log('TinyMCE initialized');

        // Register custom Fluent UI icons
        editor.ui.registry.addIcon('undo', getIconSvg(ArrowUndo24Regular));
        editor.ui.registry.addIcon('redo', getIconSvg(ArrowRedo24Regular));
        editor.ui.registry.addIcon('bold', getIconSvg(TextBold24Filled));
        editor.ui.registry.addIcon('italic', getIconSvg(TextItalic24Filled));
        editor.ui.registry.addIcon('underline', getIconSvg(TextUnderline24Filled));
        editor.ui.registry.addIcon('strikethrough', getIconSvg(TextStrikethrough24Filled));
        editor.ui.registry.addIcon('strike-through', getIconSvg(TextStrikethrough24Filled));
        editor.ui.registry.addIcon('text-color', getIconSvg(ColorFill24Filled));
        editor.ui.registry.addIcon('forecolor', getIconSvg(ColorFill24Filled));
        editor.ui.registry.addIcon('backcolor', getIconSvg(Highlight24Filled));
        editor.ui.registry.addIcon('hilitecolor', getIconSvg(Highlight24Filled));
        editor.ui.registry.addIcon('background-color', getIconSvg(Highlight24Filled));
        editor.ui.registry.addIcon('change-case', getIconSvg(TextFont24Filled));
        editor.ui.registry.addIcon('fluent-text-case', getIconSvg(TextFont24Filled));
        editor.ui.registry.addIcon('table', getIconSvg(Table24Filled));
        editor.ui.registry.addIcon('emoji', getIconSvg(Emoji24Filled));
        editor.ui.registry.addIcon('emoticons', getIconSvg(Emoji24Filled));
        editor.ui.registry.addIcon('charmap', getIconSvg(Symbols24Filled));
        editor.ui.registry.addIcon('special-character', getIconSvg(Symbols24Filled));
        editor.ui.registry.addIcon('fluent-paint-brush', getIconSvg(PaintBrush24Regular));
        editor.ui.registry.addIcon('fluent-border-all', getIconSvg(BorderAll24Filled));
        editor.ui.registry.addIcon('fluent-table-insert-row', getIconSvg(TableInsertRow24Filled));
        editor.ui.registry.addIcon('fluent-table-insert-column', getIconSvg(TableInsertColumn24Filled));
        editor.ui.registry.addIcon('fluent-table-delete-row', getIconSvg(TableDeleteRow24Filled));
        editor.ui.registry.addIcon('fluent-table-delete-column', getIconSvg(TableDeleteColumn24Filled));
        editor.ui.registry.addIcon('fluent-merge-cells', getIconSvg(TableCellsMerge24Filled));
        editor.ui.registry.addIcon('fluent-split-vertical', getIconSvg(TableCellsSplit24Filled));

        // Register case transformation menu button
        editor.ui.registry.addMenuButton('casechange', {
          icon: 'fluent-text-case',
          tooltip: 'Change case',
          fetch: function(callback) {
            const items = [
              {
                type: 'menuitem',
                text: 'lowercase',
                onAction: function() {
                  const selectedText = editor.selection.getContent({ format: 'text' });
                  if (selectedText) {
                    editor.selection.setContent(selectedText.toLowerCase());
                  }
                }
              },
              {
                type: 'menuitem',
                text: 'UPPERCASE',
                onAction: function() {
                  const selectedText = editor.selection.getContent({ format: 'text' });
                  if (selectedText) {
                    editor.selection.setContent(selectedText.toUpperCase());
                  }
                }
              },
              {
                type: 'menuitem',
                text: 'Title Case',
                onAction: function() {
                  const selectedText = editor.selection.getContent({ format: 'text' });
                  if (selectedText) {
                    const titleCase = selectedText.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
                    editor.selection.setContent(titleCase);
                  }
                }
              }
            ];
            callback(items);
          }
        });

        // =============== TABLE EDITING BUTTONS ===============

        // Helper function to get current table
        const getCurrentTable = () => {
          return editor.dom.getParent(editor.selection.getStart(), 'table');
        };

        // Helper function to get selected cells
        const getSelectedCells = () => {
          const selectedCells = [];
          const table = getCurrentTable();
          if (!table) return selectedCells;

          // First, try to get selected cells using TinyMCE's table plugin API
          const tablePlugin = editor.plugins.table;
          if (tablePlugin && typeof tablePlugin.getSelectedCells === 'function') {
            const selected = tablePlugin.getSelectedCells();
            if (selected && selected.length > 0) {
              return selected;
            }
          }

          // Fallback: Check for cells with selection classes
          const cells = table.querySelectorAll('td, th');
          cells.forEach(cell => {
            // Check multiple possible selection indicators
            if (editor.dom.hasClass(cell, 'mce-item-selected') ||
                cell.classList.contains('mce-item-selected') ||
                cell.hasAttribute('data-mce-selected') ||
                cell.getAttribute('data-mce-selected') === '1') {
              selectedCells.push(cell);
            }
          });

          // If still no cells found, check if cursor is in a cell
          if (selectedCells.length === 0) {
            const currentCell = editor.dom.getParent(editor.selection.getStart(), 'td,th');
            if (currentCell) {
              selectedCells.push(currentCell);
            }
          }

          return selectedCells;
        };

        // Cell styling menu button
        editor.ui.registry.addMenuButton('cellstyle', {
          icon: 'fluent-paint-brush',
          tooltip: 'Cell styling',
          fetch: function(callback) {
            const selectedCells = getSelectedCells();
            if (selectedCells.length === 0) {
              callback([
                {
                  type: 'menuitem',
                  text: 'No cell selected',
                  enabled: false
                }
              ]);
              return;
            }

            const items = [
              {
                type: 'menuitem',
                text: `Apply to ${selectedCells.length} cell(s)`,
                enabled: false
              },
              {
                type: 'separator'
              },
              {
                type: 'menuitem',
                text: 'Borders →',
                getSubmenuItems: function() {
                  return [
                    {
                      type: 'menuitem',
                      text: 'All sides (1px black)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border', '1px solid #000', 'important');
                          cell.style.setProperty('border-top', '1px solid #000', 'important');
                          cell.style.setProperty('border-bottom', '1px solid #000', 'important');
                          cell.style.setProperty('border-left', '1px solid #000', 'important');
                          cell.style.setProperty('border-right', '1px solid #000', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'All sides (1px gray)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border', '1px solid #ccc', 'important');
                          cell.style.setProperty('border-top', '1px solid #ccc', 'important');
                          cell.style.setProperty('border-bottom', '1px solid #ccc', 'important');
                          cell.style.setProperty('border-left', '1px solid #ccc', 'important');
                          cell.style.setProperty('border-right', '1px solid #ccc', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'separator'
                    },
                    {
                      type: 'menuitem',
                      text: 'Top only',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-top', '1px solid #000', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Bottom only',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-bottom', '1px solid #000', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Left only',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-left', '1px solid #000', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Right only',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-right', '1px solid #000', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'separator'
                    },
                    {
                      type: 'menuitem',
                      text: 'Remove all borders',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border', 'none', 'important');
                          cell.style.setProperty('border-top', 'none', 'important');
                          cell.style.setProperty('border-bottom', 'none', 'important');
                          cell.style.setProperty('border-left', 'none', 'important');
                          cell.style.setProperty('border-right', 'none', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Remove top border',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-top', 'none', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Remove bottom border',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-bottom', 'none', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Remove left border',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-left', 'none', 'important');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Remove right border',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          cell.style.setProperty('border-right', 'none', 'important');
                        });
                        editor.fire('change');
                      }
                    }
                  ];
                }
              },
              {
                type: 'separator'
              },
              {
                type: 'menuitem',
                text: 'Padding →',
                getSubmenuItems: function() {
                  return [
                    {
                      type: 'menuitem',
                      text: 'Small (5px all sides)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding', '5px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Medium (10px all sides)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding', '10px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Large (15px all sides)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding', '15px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'separator'
                    },
                    {
                      type: 'menuitem',
                      text: 'Top (10px)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding-top', '10px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Bottom (10px)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding-bottom', '10px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Left (10px)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding-left', '10px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'menuitem',
                      text: 'Right (10px)',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding-right', '10px');
                        });
                        editor.fire('change');
                      }
                    },
                    {
                      type: 'separator'
                    },
                    {
                      type: 'menuitem',
                      text: 'Remove all padding',
                      onAction: function() {
                        selectedCells.forEach(cell => {
                          editor.dom.setStyle(cell, 'padding', '0');
                        });
                        editor.fire('change');
                      }
                    }
                  ];
                }
              },
              {
                type: 'separator'
              },
              {
                type: 'menuitem',
                text: 'Light gray background',
                onAction: function() {
                  selectedCells.forEach(cell => {
                    editor.dom.setStyle(cell, 'background-color', '#f9f9f9');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Light blue background',
                onAction: function() {
                  selectedCells.forEach(cell => {
                    editor.dom.setStyle(cell, 'background-color', '#e3f2fd');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Light yellow background',
                onAction: function() {
                  selectedCells.forEach(cell => {
                    editor.dom.setStyle(cell, 'background-color', '#fff9c4');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Light green background',
                onAction: function() {
                  selectedCells.forEach(cell => {
                    editor.dom.setStyle(cell, 'background-color', '#e8f5e9');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'No background',
                onAction: function() {
                  selectedCells.forEach(cell => {
                    editor.dom.setStyle(cell, 'background-color', 'transparent');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'separator'
              },
              {
                type: 'menuitem',
                text: 'Remove all styling',
                onAction: function() {
                  selectedCells.forEach(cell => {
                    editor.dom.setAttrib(cell, 'style', null);
                  });
                  editor.fire('change');
                }
              }
            ];
            callback(items);
          }
        });

        // Toggle table borders menu button (for entire table)
        editor.ui.registry.addMenuButton('tableborders', {
          icon: 'fluent-border-all',
          tooltip: 'Table borders (entire table)',
          fetch: function(callback) {
            const table = getCurrentTable();
            if (!table) {
              callback([
                {
                  type: 'menuitem',
                  text: 'No table selected',
                  enabled: false
                }
              ]);
              return;
            }

            const items = [
              {
                type: 'menuitem',
                text: 'Show borders (1px solid)',
                onAction: function() {
                  editor.dom.setAttrib(table, 'border', '1');
                  editor.dom.setStyle(table, 'border-collapse', 'collapse');
                  editor.dom.setStyle(table, 'border', '1px solid #000');
                  const cells = table.querySelectorAll('td, th');
                  cells.forEach(cell => {
                    editor.dom.setStyle(cell, 'border', '1px solid #000');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Light borders (1px #ccc)',
                onAction: function() {
                  editor.dom.setAttrib(table, 'border', '1');
                  editor.dom.setStyle(table, 'border-collapse', 'collapse');
                  editor.dom.setStyle(table, 'border', '1px solid #ccc');
                  const cells = table.querySelectorAll('td, th');
                  cells.forEach(cell => {
                    editor.dom.setStyle(cell, 'border', '1px solid #ccc');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Shaded rows (zebra stripe)',
                onAction: function() {
                  const rows = table.querySelectorAll('tr');
                  rows.forEach((row, index) => {
                    if (index % 2 === 0) {
                      editor.dom.setStyle(row, 'background-color', '#f9f9f9');
                    } else {
                      editor.dom.setStyle(row, 'background-color', '#ffffff');
                    }
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Hide borders (borderless)',
                onAction: function() {
                  editor.dom.setAttrib(table, 'border', '0');
                  editor.dom.setStyle(table, 'border', 'none');
                  const cells = table.querySelectorAll('td, th');
                  cells.forEach(cell => {
                    editor.dom.setStyle(cell, 'border', 'none');
                  });
                  editor.fire('change');
                }
              },
              {
                type: 'menuitem',
                text: 'Remove all styling',
                onAction: function() {
                  editor.dom.setAttrib(table, 'border', null);
                  editor.dom.setAttrib(table, 'style', null);
                  const cells = table.querySelectorAll('td, th');
                  cells.forEach(cell => {
                    editor.dom.setAttrib(cell, 'style', null);
                  });
                  const rows = table.querySelectorAll('tr');
                  rows.forEach(row => {
                    editor.dom.setAttrib(row, 'style', null);
                  });
                  editor.fire('change');
                }
              }
            ];
            callback(items);
          }
        });

        // Insert row above button
        editor.ui.registry.addButton('tablerowabove', {
          icon: 'fluent-table-insert-row',
          tooltip: 'Insert row above',
          onAction: function() {
            editor.execCommand('mceTableInsertRowBefore');
          }
        });

        // Insert row below button
        editor.ui.registry.addButton('tablerowbelow', {
          icon: 'fluent-table-insert-row',
          tooltip: 'Insert row below',
          onAction: function() {
            editor.execCommand('mceTableInsertRowAfter');
          }
        });

        // Delete row button
        editor.ui.registry.addButton('tabledeleterow', {
          icon: 'fluent-table-delete-row',
          tooltip: 'Delete row',
          onAction: function() {
            editor.execCommand('mceTableDeleteRow');
          }
        });

        // Insert column left button
        editor.ui.registry.addButton('tablecolleft', {
          icon: 'fluent-table-insert-column',
          tooltip: 'Insert column left',
          onAction: function() {
            editor.execCommand('mceTableInsertColBefore');
          }
        });

        // Insert column right button
        editor.ui.registry.addButton('tablecolright', {
          icon: 'fluent-table-insert-column',
          tooltip: 'Insert column right',
          onAction: function() {
            editor.execCommand('mceTableInsertColAfter');
          }
        });

        // Delete column button
        editor.ui.registry.addButton('tabledeletecol', {
          icon: 'fluent-table-delete-column',
          tooltip: 'Delete column',
          onAction: function() {
            editor.execCommand('mceTableDeleteCol');
          }
        });

        // Merge cells button
        editor.ui.registry.addButton('tablemergecells', {
          icon: 'fluent-merge-cells',
          tooltip: 'Merge selected cells',
          onAction: function() {
            editor.execCommand('mceTableMergeCells');
          }
        });

        // Split cell button - unmerges merged cells or creates nested table
        editor.ui.registry.addButton('tablesplitcells', {
          icon: 'fluent-split-vertical',
          tooltip: 'Split cell',
          onAction: function() {
            const cell = editor.dom.getParent(editor.selection.getStart(), 'td,th');
            if (!cell) {
              editor.windowManager.alert('Please select a table cell first');
              return;
            }

            // Check if cell is merged (has colspan or rowspan > 1)
            const colspan = parseInt(cell.getAttribute('colspan') || '1');
            const rowspan = parseInt(cell.getAttribute('rowspan') || '1');

            if (colspan > 1 || rowspan > 1) {
              // Cell is merged - use TinyMCE's split command to unmerge
              editor.execCommand('mceTableSplitCells');
              return;
            }

            // Cell is not merged - show nested table dialog
            editor.windowManager.open({
              title: 'Split Cell with Nested Table',
              body: {
                type: 'panel',
                items: [
                  {
                    type: 'input',
                    name: 'rows',
                    label: 'Number of rows',
                    inputMode: 'numeric',
                    placeholder: '2'
                  },
                  {
                    type: 'input',
                    name: 'cols',
                    label: 'Number of columns',
                    inputMode: 'numeric',
                    placeholder: '2'
                  },
                  {
                    type: 'checkbox',
                    name: 'copyContent',
                    label: 'Copy existing content to first cell'
                  }
                ]
              },
              buttons: [
                {
                  type: 'cancel',
                  text: 'Cancel'
                },
                {
                  type: 'submit',
                  text: 'Split',
                  primary: true
                }
              ],
              onSubmit: function(api) {
                const data = api.getData();
                const rows = parseInt(data.rows) || 2;
                const cols = parseInt(data.cols) || 2;
                const copyContent = data.copyContent;

                if (rows < 1 || rows > 10 || cols < 1 || cols > 10) {
                  editor.windowManager.alert('Please enter numbers between 1 and 10');
                  return;
                }

                // Get current cell styles
                const computedStyle = window.getComputedStyle(cell);
                const cellBgColor = computedStyle.backgroundColor;
                const cellFontSize = computedStyle.fontSize;
                const cellFontFamily = computedStyle.fontFamily;
                const cellColor = computedStyle.color;

                // Get parent table border style to match it
                const parentTable = editor.dom.getParent(cell, 'table');
                const parentCells = parentTable?.querySelectorAll('td, th');
                let parentCellBorder = '1px solid #ccc'; // default
                if (parentCells && parentCells.length > 0) {
                  const parentCellStyle = window.getComputedStyle(parentCells[0]);
                  parentCellBorder = parentCellStyle.border || '1px solid #ccc';
                }

                // Save the existing content
                const existingContent = copyContent ? cell.innerHTML : '';

                // Remove all padding from parent cell to make nested table fit perfectly
                editor.dom.setStyle(cell, 'padding', '0');
                editor.dom.setStyle(cell, 'margin', '0');

                // Create nested table HTML with invisible styling
                let nestedTableHTML = `<table style="
                  width: 100%;
                  height: 100%;
                  border-collapse: collapse;
                  border: none;
                  margin: 0;
                  padding: 0;
                  background-color: ${cellBgColor};
                  font-size: ${cellFontSize};
                  font-family: ${cellFontFamily};
                  color: ${cellColor};
                ">`;

                for (let i = 0; i < rows; i++) {
                  nestedTableHTML += '<tr>';
                  for (let j = 0; j < cols; j++) {
                    const cellContent = (i === 0 && j === 0 && copyContent) ? existingContent : '&nbsp;';

                    // Only add internal borders that mimic cell splits
                    // Remove outer borders to blend with parent cell
                    let borderStyle = '';
                    if (i < rows - 1) borderStyle += `border-bottom: ${parentCellBorder};`;
                    if (j < cols - 1) borderStyle += `border-right: ${parentCellBorder};`;

                    nestedTableHTML += `<td style="
                      ${borderStyle}
                      padding: 5px;
                      margin: 0;
                      background-color: inherit;
                      vertical-align: top;
                    ">${cellContent}</td>`;
                  }
                  nestedTableHTML += '</tr>';
                }
                nestedTableHTML += '</table>';

                // Replace cell content with nested table
                cell.innerHTML = nestedTableHTML;

                api.close();
                editor.fire('change');
              }
            });
          }
        });

        // Custom cell split function
        // editor.ui.registry.addButton('customsplitcell', {
        //   text: 'Split Cell',
        //   tooltip: 'Split cell into rows or columns',
        //   onAction: function() {
        //     const cell = editor.dom.getParent(editor.selection.getStart(), 'td,th');
        //     if (!cell) {
        //       editor.windowManager.alert('Please select a table cell first');
        //       return;
        //     }

        //     editor.windowManager.open({
        //       title: 'Split Cell',
        //       body: {
        //         type: 'panel',
        //         items: [
        //           {
        //             type: 'selectbox',
        //             name: 'direction',
        //             label: 'Split Direction',
        //             items: [
        //               { value: 'horizontal', text: 'Horizontally (create rows)' },
        //               { value: 'vertical', text: 'Vertically (create columns)' }
        //             ]
        //           },
        //           {
        //             type: 'input',
        //             name: 'count',
        //             label: 'Number of cells',
        //             inputMode: 'numeric'
        //           }
        //         ]
        //       },
        //       buttons: [
        //         {
        //           type: 'cancel',
        //           text: 'Cancel'
        //         },
        //         {
        //           type: 'submit',
        //           text: 'Split',
        //           primary: true
        //         }
        //       ],
        //       onSubmit: function(api) {
        //         const data = api.getData();
        //         const count = parseInt(data.count) || 2;
        //         const direction = data.direction;

        //         if (count < 2 || count > 10) {
        //           editor.windowManager.alert('Please enter a number between 2 and 10');
        //           return;
        //         }

        //         if (direction === 'horizontal') {
        //           // Split horizontally (add rows)
        //           const row = cell.parentNode;
        //           const cellIndex = Array.from(row.cells).indexOf(cell);

        //           for (let i = 0; i < count - 1; i++) {
        //             const newRow = row.cloneNode(false);
        //             for (let j = 0; j < row.cells.length; j++) {
        //               const newCell = editor.dom.create(cell.nodeName);
        //               newCell.innerHTML = '&nbsp;';
        //               if (j !== cellIndex) {
        //                 // Extend existing cells vertically
        //                 const existingCell = row.cells[j];
        //                 const currentRowspan = parseInt(existingCell.getAttribute('rowspan') || '1');
        //                 existingCell.setAttribute('rowspan', currentRowspan + 1);
        //               } else {
        //                 newRow.appendChild(newCell);
        //               }
        //             }
        //             if (newRow.cells.length > 0) {
        //               row.parentNode.insertBefore(newRow, row.nextSibling);
        //             }
        //           }
        //           cell.setAttribute('rowspan', count);
        //         } else {
        //           // Split vertically (add columns)
        //           const cellContent = cell.innerHTML;
        //           cell.innerHTML = '&nbsp;';

        //           for (let i = 0; i < count - 1; i++) {
        //             const newCell = editor.dom.create(cell.nodeName);
        //             newCell.innerHTML = '&nbsp;';
        //             cell.parentNode.insertBefore(newCell, cell.nextSibling);
        //           }

        //           cell.setAttribute('colspan', count);

        //           // Update other rows to add matching cells
        //           const table = editor.dom.getParent(cell, 'table');
        //           const rows = table.querySelectorAll('tr');
        //           const currentRow = cell.parentNode;
        //           const cellIndex = Array.from(currentRow.cells).indexOf(cell);

        //           rows.forEach(row => {
        //             if (row !== currentRow) {
        //               const refCell = row.cells[cellIndex];
        //               if (refCell) {
        //                 for (let i = 0; i < count - 1; i++) {
        //                   const newCell = editor.dom.create('td');
        //                   newCell.innerHTML = '&nbsp;';
        //                   row.insertBefore(newCell, refCell.nextSibling);
        //                 }
        //               }
        //             }
        //           });
        //         }

        //         api.close();
        //         editor.fire('change');
        //       }
        //     });
        //   }
        // });


        // Add auto-save listeners if callback is provided
        if (onAutoSave) {
          const scheduleAutoSave = () => {
            // Clear existing timer
            if (autoSaveTimerRef.current) {
              clearTimeout(autoSaveTimerRef.current);
            }

            // Schedule auto-save after delay
            autoSaveTimerRef.current = setTimeout(() => {
              const content = editor.getContent();
              console.log('🔄 Auto-saving to backend...');
              onAutoSave(content);
            }, autoSaveDelay);
          };

          // Listen to content changes
          editor.on('keyup', scheduleAutoSave);
          editor.on('change', scheduleAutoSave);
          editor.on('paste', scheduleAutoSave);

          // Clean up on editor destroy
          editor.on('remove', () => {
            if (autoSaveTimerRef.current) {
              clearTimeout(autoSaveTimerRef.current);
            }
            editor.off('keyup', scheduleAutoSave);
            editor.off('change', scheduleAutoSave);
            editor.off('paste', scheduleAutoSave);
          });
        }

        // Add selection change listeners if callback is provided
        if (onSelectionChange) {
          const handleSelectionChange = () => {
            onSelectionChange(editor);
          };

          editor.on('selectionchange', handleSelectionChange);
          editor.on('keyup', handleSelectionChange);
          editor.on('mouseup', handleSelectionChange);

          // Clean up listeners when editor is destroyed
          editor.on('remove', () => {
            editor.off('selectionchange', handleSelectionChange);
            editor.off('keyup', handleSelectionChange);
            editor.off('mouseup', handleSelectionChange);
          });
        }
      }}
      initialValue={contentToUse}
      disabled={disabled}
      onEditorChange={onEditorChange}
      init={{
        height: height,
        menubar: true,
        plugins: [
          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
          'insertdatetime', 'media', 'table', 'help', 'wordcount', 'emoticons'
        ],

        // Microsoft Word-style tabbed toolbar
        toolbar_mode: 'sliding',
        toolbar_groups: {
          home: {
            title: 'Home',
            items: 'undo redo | blocks fontsizeinput | bold italic underline strikethrough | forecolor backcolor | casechange | alignleft aligncenter alignright alignjustify | lineheight | removeformat | fullscreen'
          },
          insert: {
            title: 'Insert',
            items: 'table image link emoticons charmap hr anchor insertdatetime'
          },
          layout: {
            title: 'Layout',
            items: 'bullist numlist | outdent indent | tableborders cellstyle | tablerowabove tablerowbelow tabledeleterow | tablecolleft tablecolright tabledeletecol | tablemergecells tablesplitcells'
          },
          view: {
            title: 'View',
            items: 'visualblocks | searchreplace'
          }
        },

        // Fallback to single toolbar if toolbar_groups not supported
        toolbar: [
          {
            name: 'history', items: [ 'undo', 'redo' ]
          },
          {
            name: 'formatting', items: [ 'blocks', 'fontsizeinput', 'bold', 'italic', 'underline', 'strikethrough', 'forecolor', 'backcolor' ]
          },
          {
            name: 'textcase', items: [ 'casechange' ]
          },
          {
            name: 'alignment', items: [ 'alignleft', 'aligncenter', 'alignright', 'alignjustify', 'lineheight' ]
          },
          {
            name: 'indentation', items: [ 'outdent', 'indent', 'bullist', 'numlist' ]
          },
          {
            name: 'insert', items: [ 'table', 'image', 'link', 'emoticons', 'charmap', 'hr' ]
          },
          {
            name: 'tabletools', items: [ 'tableborders', 'cellstyle' ]
          },
          {
            name: 'tablerows', items: [ 'tablerowabove', 'tablerowbelow', 'tabledeleterow' ]
          },
          {
            name: 'tablecols', items: [ 'tablecolleft', 'tablecolright', 'tabledeletecol' ]
          },
          {
            name: 'tablecells', items: [ 'tablemergecells', 'tablesplitcells' ]
          },
          {
            name: 'view', items: [ 'visualblocks' ]
          },
          {
            name: 'cleanup', items: [ 'removeformat', 'fullscreen' ]
          }
        ],


        branding: false,
        promotion: false,

        // Fix for fullscreen mode - ensure UI elements appear in fullscreen
        ui_mode: 'split',
        inline_boundaries: false,

        // Additional settings for better HTML handling
        valid_elements: '*[*]', // Allow all elements and attributes
        extended_valid_elements: '*[*]', // Additional validation for custom elements
        entity_encoding: 'raw', // Keep HTML entities as is
        encoding: 'html', // Use HTML encoding
        convert_fonts_to_spans: true,
        browser_spellcheck: true,

        // Preserve line breaks and whitespace
        forced_root_block: false, // Don't wrap content in paragraphs automatically
        force_br_newlines: true, // Use <br> for line breaks instead of <p>
        force_p_newlines: false, // Don't force paragraph tags
        remove_linebreaks: false, // Keep line breaks in source
        convert_newlines_to_brs: true, // Convert \n to <br> tags
        remove_trailing_brs: false, // Don't remove trailing <br> tags
        trim_span_elements: false, // Don't remove empty span elements
        verify_html: false, // Don't verify/clean HTML
        element_format: 'html', // Preserve original HTML format

        // Apply custom styles from original HTML
        content_style: stylesToApply,

        line_height_formats: '1 1.15 1.5 2 2.5 3',

        // Preserve style tags in content
        allow_html_in_named_anchor: true,
        preserve_whitespace: true,
        // Disable automatic cleanup of content
        cleanup: false,

        // Table configuration for split cells support
        table_cell_advtab: true,
        table_row_advtab: true,
        table_advtab: true,

        // Add table context menu with insert row/column options
        table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',

      }}
    />
  );
};

export default TinyMCEEditor;